// src/pages/Admin/Settings/components/SiteQrCodeSection.jsx

import React, { useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  FiCopy,
  FiDownload,
  FiExternalLink,
  FiInfo,
  FiLink,
  FiRefreshCw,
} from "react-icons/fi";

import SectionCard from "./SectionCard";

const DEFAULT_SITE_URL = process.env.REACT_APP_URL || "";

const normalizeUrl = (value) => {
  const clean = String(value || "").trim();

  if (!clean) return "";

  if (/^https?:\/\//i.test(clean)) {
    return clean;
  }

  return `https://${clean}`;
};

export default function SiteQrCodeSection({ siteUrl = DEFAULT_SITE_URL }) {
  const qrRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const finalUrl = useMemo(() => normalizeUrl(siteUrl), [siteUrl]);

  const handleCopy = async () => {
    if (!finalUrl) return;

    try {
      await navigator.clipboard.writeText(finalUrl);
      setCopied(true);

      window.setTimeout(() => {
        setCopied(false);
      }, 1800);
    } catch (error) {
      console.error("Erreur copie URL QR code:", error);
    }
  };

  const handleDownloadSvg = () => {
    if (!finalUrl || !qrRef.current) return;

    const svg = qrRef.current.querySelector("svg");

    if (!svg) return;

    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);

    const blob = new Blob([source], {
      type: "image/svg+xml;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "qr-code-site-mairie.svg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  return (
    <SectionCard
      title="QR code du site"
      description="QR code public à imprimer ou partager pour accéder rapidement au site de la mairie."
    >
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
        <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
          <div className="flex aspect-square items-center justify-center rounded-[24px] bg-white p-5 shadow-sm dark:bg-white">
            {finalUrl ? (
              <div ref={qrRef} className="flex items-center justify-center">
                <QRCodeSVG
                  value={finalUrl}
                  size={220}
                  level="H"
                  marginSize={2}
                  bgColor="#ffffff"
                  fgColor="#0f172a"
                  title="QR code du site de la mairie"
                />
              </div>
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center text-center text-slate-400">
                <FiRefreshCw className="text-3xl" />
                <p className="mt-3 text-sm font-bold">
                  URL du site non configurée
                </p>
              </div>
            )}
          </div>

          <p className="mt-3 text-center text-xs leading-5 text-slate-500 dark:text-slate-400">
            Format SVG haute qualité, idéal pour impression.
          </p>
        </div>

        <div className="flex flex-col justify-between rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-1 text-xs font-bold text-sky-700 dark:bg-sky-950/40 dark:text-sky-200">
              <FiInfo />
              Lien public
            </div>

            <h3 className="mt-4 text-lg font-bold text-slate-950 dark:text-slate-50">
              Site internet de la mairie
            </h3>

            <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
              Ce QR code permet d’accéder rapidement au site de la mairie. Il peut être affiché en mairie, sur des flyers, panneaux
              d’information ou documents municipaux.
            </p>

            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
              <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                <FiLink />
                URL utilisée
              </div>

              <div className="break-all text-sm font-bold text-slate-800 dark:text-slate-100">
                {finalUrl || "REACT_APP_URL non renseignée"}
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <button
              type="button"
              onClick={handleCopy}
              disabled={!finalUrl}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FiCopy />
              {copied ? "Lien copié" : "Copier le lien"}
            </button>

            <button
              type="button"
              onClick={handleDownloadSvg}
              disabled={!finalUrl}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-sky-600 px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FiDownload />
              Télécharger le QR
            </button>

            {finalUrl ? (
              <a
                href={finalUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FiExternalLink />
                Ouvrir le site
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </SectionCard>
  );
}